import { SYSConfig } from "../../config"
import {
  DisplayFormats,
  getDateISOString,
  getDateValue,
  getDecimalValue,
  getIntegerValue,
  stringifyReplacer,
  T,
} from "../../global/globals"

import {
  HookFieldGetList,
  HookFormAutoInit,
  HookFormAutoInit_Type,
  HookFormChanged,
  HookFormEditorInit,
  HookFormInit,
  HookFormRemove,
  HookFormValidate,
  HookFormValidatorsDefaults,
  HookViewHeaderCellClass,
  HookViewRowCellClass,
} from "../../global/hooks"

import type {
  FieldValueChanged,
  HookFieldGetList_ReturnType,
  HookFormChanged_Type,
  HookFormEditorInit_Type,
  HookFormInit_ReturnType,
  HookFormInit_Type,
  HookFormRemove_Type,
  HookFormValidate_Type,
  HookFormValidatorsDefaults_Type,
  HookViewHeaderCellClass_Type,
  HookViewRowCellClass_Type,
  ValidatorCheckOptions,
} from "../../global/hooks"

import {
  ovl,
  type SYSAction,
  type SYSActions,
  type SYSEditor,
  type SYSFeature,
  type SYSForm,
  type SYSFormValidationValidators,
  type SYSState,
  type SYSTableDefIds,
} from "../../"

import {
  _cachedFieldLists,
  _GetFieldList,
  getDisplayValue,
} from "../table/helpers"
import type {
  _ColumnAlign,
  ColumnDisplayDef,
  ControlType,
  EditDisplayMode,
  EditGroups,
  EditorType,
  ListDefinition,
  SYSInputMode,
  ViewRowClassType,
} from "../table/table"
import { _FillListControl } from "./controls/actions"
import {
  getColumnDefsFromFormState,
  GetFieldLabel,
  GetRowFromFormState,
} from "./controls/helpers"
import type { ListState } from "./controls/listcontrol"
import {
  FieldCurrentList,
  FieldIsValid,
  FieldIsValidSelective,
  FieldList,
  FieldReset,
  FieldSetState,
  FieldSetVal,
  FieldValidate,
} from "./fieldapi"
import {
  _resetValidationForContext,
  _SetCorrectedValue,
  _setFieldTouched,
  _setFieldWatched,
  getFormFields,
  sortedReplacer,
} from "./helper"
import type {
  DataType,
  FieldFormat,
  FieldSchema,
  Schema,
  SYSFormField,
} from "./sysformelement"
import type {
  FieldValidationResult,
  OptionsValidate,
  OptionsValidateResultError,
  ValidateResult,
  ValidateResultError,
  ValidateResultVisibleSummaryError,
  ValidateSummaryResult,
  ValidationResetContext,
  ValidationsAddOptions,
  ValidatorOptions,
  ValidatorUseOptions,
} from "./validators"
import {
  _setFieldValid,
  _setFormValid,
  _validationsAdd,
  _validatorUse,
  /*setFormDirty,*/
  setVisibleValidation,
  ValidationsRemove,
} from "./validators"

import { SortGet } from "../../global/globalapi"

import { actionTracking, StartScreenUpdate } from "../../tracker/proxyhandler"
import { _RenderTracking } from "../../tracker/tracker"
import {
  TableGet,
  _TableGetReturnType,
  TableSchemaGet,
} from "../table/tableapi"
import {
  ValidationSummaryAdd,
  ValidationsAdd,
  ValidatorAdd,
  ValidatorCheck,
} from "./validatorsapi"
import { FormInit, FormIsValid } from "./formapi"
import { MergeDeep } from "../../global/actions"
import { SnackAdd } from "../snackapi"
import { debounceInfo } from "./controls/textbox"

export { _FillListControl }
export type AutoComplete =
  | "off"
  | "on"
  | "name"
  | "honorific-prefix"
  | "given-name"
  | "additional-name"
  | "family-name"
  | "honorific-suffix"
  | "nickname"
  | "email"
  | "username"
  | "new-password"
  | "current-password"
  | "one-time-code"
  | "organization-title"
  | "organization"
  | "street-address"
  | "country"
  | "country-name"
  | "postal-code"
  | "cc-name"
  | "cc-given-name"
  | "cc-additional-name"
  | "cc-family-name"
  | "cc-number"
  | "cc-exp"
  | "cc-exp-month"
  | "cc-exp-year"
  | "cc-csc"
  | "cc-type"
  | "transaction-currency"
  | "transaction-amount"
  | "language"
  | "bday"
  | "bday-day"
  | "bday-month"
  | "bday-year"
  | "sex"
  | "tel"

export type SYSField = {
  order?: number
  control?: ControlType
  value: string

  previousValue: string
  convertedValue: any
  originalValue: any
  type: DataType

  valid: boolean
  // touched is used just for listControl now. and it doesn't mean its touched so its misleading
  touched: boolean
  watched: boolean
  hasFocus?: boolean
  inactive?: boolean

  // removed in favor of schema.isCalculated
  //ignoreForFormValid?: boolean

  inactiveButRelevantForValidation?: boolean
  validationResult: FieldValidationResult
  id: string
  formType: string
  formId: string
  fieldKey: string
  groups: {
    tab: number
    group: number
  }
  nullHandling: {
    enabled: boolean
    setToNullIf?: any
    setToValueIfNull?: any
  }
  schema: FieldSchema

  ui?: {
    labelTranslationKey?: string
    labelCaption?: string
    noLabel?: boolean
    format?: FieldFormat
    align?: _ColumnAlign
    option?: { inline?: boolean }
    isPassword?: boolean
    readonly?: boolean
    alwaysReadOnly?: boolean
    checkedValue?: any
    uncheckedValue?: any
    useSpellcheck?: boolean
    autocomplete?: AutoComplete
    visible?: ControlVisiblity
    useBrowserDatePicker?: boolean
    fillSpaceIfNotVisible?: boolean
    displayEmptyIfValue?: any
    displayValueHintWhileTyping?: boolean
    infoValue?: string
    selectIfFocused?: boolean
    inputMode?: SYSInputMode
    // use debounce eg. for fast pacing HID scanner input
    inputDebounceMS?: number
    addIgnoreFocusClass?: boolean
  }
  previousConvertedValue: any
  previousValid: boolean
  previousValidConvertedValue: any
  customState: any
  _state?: {
    // use forcesave for client values filled and not saved (eg. usecase permissions were we build a table from template values only on client. in that case the dirty ccheck and value compare would fail and report no changes)
    forceSave: boolean
    visible: boolean
    closing: boolean
    removedAndIrrelevantInCurrentSituation: boolean
    forceChange: boolean
    valueAfterGotFocus: any
    valueChangedHandled?: boolean
    //valueCorrected?: boolean
    fieldInitialValidated?: boolean
    fieldInitialChanged?: boolean
    autoFillHandled?: boolean
    noRetrigger?: boolean

    // needed for EditorFields...They need to save groupState for creating a pseudo Tabledef for BodyRendering
    groupState?: EditGroups[]
    _files?: File[]
  }
  list?: ListState

  editor?: EditorType
}

export type ControlVisiblity = "true" | "false" | "fadeIn" | "fadeOut"
export type ActiveInactiveVisiblity =
  | "noVisibilityChange"
  | "true/false"
  | "fadeIn/fadeOut"
  | "true/readonly"

// export type BuiltInValidationDisplayType = {
//   dataTypeValidation?: ValidationSettingsType
//   schemaValidation?: ValidationSettingsType
//   listValidation?: ValidationSettingsType
//   customValidationDefaults?: CustomValidationSettingsType
// }

// export type CustomValidationSettingsType = {
//   field: {
//     displayType: FormValidationDisplayType
//     displayTypeIfSummary: FormValidationDisplayType
//   }
//   summary?: {
//     displayType: FormValidationSummaryDisplayType
//   }
// }

// export type ValidationSettingsType = {
//   isGrouped?: boolean
//   _validationGroup?: string
//   field: {
//     displayType: FormValidationDisplayType
//   }
//   summary?: {
//     displayType: FormValidationSummaryDisplayType
//     msg?: { translationKey: string }
//   }
// }
export type FieldValueMap = { [key: string]: SYSField }
type FormFieldValueMap = { [key: string]: SYSFormField }
export type SummaryErrorList = {
  translationKey: string
  translationReps: string[]
  involvedFieldKeys?: string[]
  involvedFieldKeysMinusFocused?: string[]
  displayAlways?: boolean
}

export type SYSFormState = {
  valid: boolean
  fields: FieldValueMap
  formType: SYSForm
  formId: string
  parent?: {
    formType?: SYSForm
    formId?: string
    fieldKey: string
  }
  initFields: FieldValueMap
  initialFields: FormFieldValueMap
  feature: SYSFeature
  subFeature: string
  schema: { [key: string]: Schema }
  validationDefault: OptionsValidate
  forceFocusWhenVisibleFieldKey: string
  firstEditableFocusSet?: boolean

  validationResult?: {
    // 1st key is the tab
    [key: string]: {
      //key is translationKey for now (with future enhancement for a free group)
      summaryErrors: {
        [key: string]: SummaryErrorList
      }
      //key is translationKey for now (with future enhancement for a free group)
      validationKeyErrors: {
        [key: string]: SummaryErrorList
      }
      // field errors is just an array
      fieldErrors: SummaryErrorList[]
    }
  }
  tableDefId?: SYSTableDefIds
  tableRowKey?: string
  tableDataMode?: "Add" | "Update"
  isInline?: boolean
  row?: any

  // locked eg. when form is saving nothing should be changed
  locked?: boolean
  firstInitOrSituationChanged?: boolean
  forceKeepFormStateOnCancel?: boolean
  doFormChangedForEveryFieldInInit?: boolean
  useAlignRight?: boolean

  _state: {
    _oldSituation: string
    _fieldSetValFromDebouncedInit?: boolean
    _viewRowCell?: ViewRowClassType
    _viewHeaderCell?: ViewRowClassType
    _activeEditTab?: number
    _firstTimeToUpdate?: boolean
    _noCancelOnEditFormIfFormStateWasInvalidWhenOpened?: boolean
    _summaryDebouncer: number
    _inInitOrChangedCount: number
  }

  groups?: {
    edit?: {
      [key in number]: {
        visible?: "true" | "false"
        inactive?: boolean
        closing?: boolean
        removedAndIrrelevantInCurrentSituation?: boolean
      }
    }
  }
}
type FormStatePerInstance = {
  // key corresponds here to instanceId of form
  [key: string]: SYSFormState
}

export type InitFormType = {
  formType: SYSForm
  formId?: string
  fields: { [key: string]: SYSFormField }
  feature?: SYSFeature
  subFeature?: string
  forceOverwrite?: boolean
  forceKeepFormStateOnCancel?: boolean
  doFormChangedForEveryFieldInInit?: boolean
  initialFocusFieldKey?: string
  tableDefId?: SYSTableDefIds
  tableRowKey?: string
  tableDataMode?: "Add" | "Update"
  isInline?: boolean
  row?: any
  parentFormType?: SYSForm
  parentFormId?: string
  parentFieldKey?: string
  _fromAutoInit?: boolean
  useAlignRight?: boolean
  fillSpaceIfEmptyField?: boolean
}

export type FormsState = { [key in SYSForm]: FormStatePerInstance }

export const _ResetForm: SYSAction<SYSFormState> = (value) => {
  if (value.validationResult) {
    Object.keys(value.validationResult).forEach((k) => {
      value.validationResult[k].fieldErrors = []
      value.validationResult[k].summaryErrors = {}
      value.validationResult[k].validationKeyErrors = {}
    })
  }
  Object.keys(value.fields).forEach((k) => {
    FieldReset(value.fields[k])
  })
  value.valid = true
  //value.dirty = false
}

export const _RemoveForm: SYSAction<{ formType: string; formId: string }> = (
  value,
  { global }
) => {
  let state = global.getState()
  let actions = global.getActions()
  let forms = state.ovl.forms
  let f: SYSFormState = forms[value.formType]?.[value.formId]
  if (f) {
    // call FormRemove Hook
    let fn =
      actions.app.features[f.feature]?.hooks?.[
        HookFormRemove.replace("{subFeature}", f.subFeature)
      ]
    if (fn) {
      fn(<HookFormRemove_Type>{
        formState: f,
        fields: f.fields,
      })
    }
  }
  if (f && !f.forceKeepFormStateOnCancel) {
    Object.keys(f.fields).forEach((k) => {
      f.fields[k]._state._files = undefined
    })

    delete forms[value.formType][value.formId]
  }
}

// export const _SetFormUndirty: SYSAction<SYSFormState> = (value) => {
//   if (value.locked) {
//     value.locked = false
//   }
//   Object.keys(value.fields).forEach((e) => {
//     let field = value.fields[e]
//     field.originalValue = JSON.parse(JSON.stringify(field.value))
//     if (field.touched) {
//       field.touched = false
//     }
//   })
// }

export const _SetFormLock: SYSAction<{
  formState: SYSFormState
  locked: boolean
}> = (value) => {
  if (value.locked !== value.formState.locked) {
    value.formState.locked = value.locked
  }
}

// export const _SetFormDirty: SYSAction<SYSFormState> = (value) => {
//   if (!value.dirty) {
//     value.dirty = true
//   }
// }

// export const _SetFieldDirty: SYSAction<{ field: SYSField; dirty: boolean }> = ({
//   field,
//   dirty,
// }) => {
//   field.dirty = dirty
// }

export const _SetFieldTouched: SYSAction<{
  field: SYSField
  touched: boolean
}> = ({ field, touched }) => {
  field.touched = touched
}

export const _ValidateDataType: SYSAction<ValidateFieldType> = (
  value,
  { global }
) => {
  let actions = global.getActions()
  let field = value.field
  let type = field.type
  let format
  if (field.ui.format) {
    format = field.ui.format
  }
  let val: any = field.value

  if (value.isInnerEvent && field.list) {
    field.convertedValue = field.value
  }

  if (field.type !== "text" && val === "") {
    val = null
  }
  if (!value.isInnerEvent) {
    let hardValueSet = false

    if (field.nullHandling.setToNullIf !== undefined) {
      if (val === field.nullHandling.setToNullIf) {
        //field.dirty = true
        field.value = null
        field.convertedValue = field.value
        val = field.value
        hardValueSet = true
      }
    }
    if (field.nullHandling.setToValueIfNull !== undefined) {
      if (val === null) {
        //field.dirty = true
        field.value = field.nullHandling.setToValueIfNull
        field.convertedValue = field.value
        val = field.value
        hardValueSet = true
      }
    }

    if (hardValueSet) {
      return
    }
  }
  if (!value.isInnerEvent && field.list && val !== undefined) {
    let functionName = HookFieldGetList.replace(
      "{subFeature}",
      value.formState.subFeature
    ).replace("{fieldKey}", field.fieldKey)
    let listdata: HookFieldGetList_ReturnType
    let fn = actions.app.features[value.formState.feature]
    if (
      field.ui.displayEmptyIfValue !== undefined &&
      val === field.ui.displayEmptyIfValue
    ) {
      field.value = undefined
    } else if (fn?.hooks?.[functionName]) {
      listdata = _GetFieldList(
        fn.hooks[functionName],
        field.fieldKey,
        value.formState,
        value.formState?.tableDefId,
        undefined,
        true
      )

      let entry = listdata.data[val]
      if (entry) {
        // we really assume here that the type is correct
        field.value = entry[field.list.displayField]
        if (entry[field.list.valueField] !== undefined) {
          field.convertedValue = entry[field.list.valueField]
          // listvalues are always assumed to be of correct type
          return
        } else {
          field.convertedValue = val
        }
      } else {
        field.convertedValue = val
      }
    }
    if (field.list.acceptOnlyListValues) {
      // further checks will be done in subsequent _ValidateList
      // but if it allows values which are not in the list we need to proceed with datatype check
      return
    }
  }

  // only do type validation if there is a value
  // other scenarios should be handled in the custom validation
  switch (type) {
    case "object": {
      if (!value.isInnerEvent) {
        if (typeof val === "string") {
          field.convertedValue = JSON.parse(val)
        } else if (val === null) {
          field.convertedValue = null
        }
      }
      break
    }
    case "bool": {
      if (val !== field.convertedValue && !value.isInnerEvent) {
        field.convertedValue = val
      }
      break
    }
    case "asset":
    case "text":
      if (!value.isInnerEvent) {
        field.convertedValue = val
      }
      break

    case "inttime":
      if (val) {
        if (!value.isInnerEvent) {
          timehelper(field, val, true)
        }
      } else {
        if (!value.isInnerEvent) {
          field.convertedValue = null
        }
        field.value = null
      }
      break

    case "time":
      if (val) {
        if (!value.isInnerEvent) {
          timehelper(field, val, false)
        }
      } else {
        if (!value.isInnerEvent) {
          field.convertedValue = null
        }
        field.value = null
      }
      break

    case "date":
      if (val) {
        if (
          (val.length === 10 || val.length === 24 || val.length === 19) &&
          (val.indexOf("-") > -1 || val.indexOf("/") > -1)
        ) {
          // looks like the well formed date select format coming from chooser
          if (val.indexOf("/") > -1) {
            val.replace("/", "-")
          }
          let parts = val.split("-")
          //its the one if we have year 4 digits
          if (parts[0].length === 4) {
            let newDate = new Date(
              parseInt(parts[0]),
              parseInt(parts[1]) - 1,
              parseInt(parts[2])
            )

            field.convertedValue = getDateISOString(newDate)

            field.value = getDateValue(field.convertedValue, format)
            return
          }
        }

        let vals
        let dayFirst = DisplayFormats.dateSpecifics.dayFirst
        if (val.indexOf(".") > 0) {
          vals = val.split(".")
        } else {
          vals = val.split("/")
        }
        // there should be 3 entries (dd,mm,yyyy) for a complete date as supported for now
        let cDate = new Date()
        let cDay = cDate.getDate()
        let cMonth = cDate.getMonth() + 1
        let cYear = cDate.getFullYear()

        let day
        let month
        let year

        if (vals.length === 1) {
          day = parseInt(vals[0])
        } else if (vals.length > 1) {
          if (vals.length > 2) {
            year = parseInt(vals[2])
            if (year.toString().length < 4) {
              year += 2000
            }
          }
          if (!dayFirst) {
            day = parseInt(vals[1])
            month = parseInt(vals[0])
          } else {
            month = parseInt(vals[1])
            day = parseInt(vals[0])
          }
        }
        let newDate =
          (year ? year.toString().padStart(4, "0") : cYear.toString()) +
          "-" +
          (month
            ? month.toString().padStart(2, "0")
            : cMonth.toString().padStart(2, "0")) +
          "-" +
          (day
            ? day.toString().padStart(2, "0")
            : cDay.toString().padStart(2, "0")) +
          "T00:00:00"
        let resp = new Date(newDate).getTime()
        if (!resp) {
          ValidationsAdd(field, "SYSValidatorDataTypeDate", "System")
        } else {
          let dval = new Date(resp)
          newDate = getDateISOString(dval)
          if (!value.isInnerEvent) {
            field.convertedValue = newDate
            field.value = getDateValue(dval, format)
          }
        }
      } else {
        if (!value.isInnerEvent) {
          field.convertedValue = null
        }
        field.value = null
      }

      break

    case "datetime": {
      // assume this only comes from the datetime-local control so far
      // so just add timezone info
      if (!value.isInnerEvent) {
        if (!field.value) {
          field.convertedValue = null
        } else {
          try {
            if (
              SYSConfig._system.defaultDateTimeControlSubmitType === "Offset"
            ) {
              // keep the users timezone info (DateTimeOffset db type)
              field.convertedValue = toIsoString(new Date(field.value))
            } else {
              // create utc (should work with DateTime db type becuase datekind will be UTC)
              field.convertedValue = new Date(field.value).toISOString()
            }
          } catch {
            ValidationsAdd(field, "SYSValidatorDataTypeDate", "System")
          }
        }
      }
      break
    }

    case "int": {
      if (val || val === 0) {
        let parsedVal = parseInt(val)
        //if (parsedVal.toString() !== val.toString()) {
        if (parsedVal || parsedVal === 0) {
          if (!value.isInnerEvent) {
            let pval = getIntegerValue(parsedVal, format)
            if (pval !== field.value) {
              field.value = pval
            }
          } else {
            if (val !== field.value) {
              field.value = val
            }
          }
          if (field.convertedValue !== parsedVal && !value.isInnerEvent) {
            field.convertedValue = parsedVal
          }
          //RemoveFieldValidation(field, "AppValidationInvalidNumberFormat")
        } else {
          ValidationsAdd(field, "SYSValidatorDataTypeNumber", "System")
        }
      } else {
        if (!value.isInnerEvent) {
          field.convertedValue = null
        }
        field.value = null
      }
      break
    }

    case "decimal": {
      if (val || val === 0) {
        let parsedVal
        if (typeof val === "string") {
          let valToParse = val.replace(/[^0-9.,-]/g, "")
          valToParse = valToParse.replace(/[,]/g, ".")
          let splits = valToParse.split(".")
          if (splits.length > 1) {
            let fixed = splits.map((m, i) => {
              if (i === splits.length - 1) {
                return `.${m}`
              }
              return m
            })
            parsedVal = parseFloat(fixed.join(""))
          } else {
            parsedVal = parseFloat(valToParse)
          }
        } else {
          parsedVal = val
        }
        if (parsedVal || parsedVal == 0) {
          if (!value.isInnerEvent) {
            // as long as we are entering the value digit by digit we don't like formatting
            field.value = getDecimalValue(parsedVal, format) //parsedVal.toString()
            field.convertedValue = parsedVal
          }

          //RemoveFieldValidation(field, "AppValidationInvalidNumberFormat")
        } else {
          ValidationsAdd(field, "SYSValidatorDataTypeNumber", "System")
        }
      } else {
        if (!value.isInnerEvent) {
          field.convertedValue = null
        }
        field.value = null
      }
      break
    }
  }
}

const timehelper = (field: SYSField, val: string, asInt: boolean) => {
  let valLeftInt = 0
  let valRightInt = 0
  val = val.replace("+", "").replace("-", "")
  let splitter = val.indexOf(":") > -1 ? ":" : val.indexOf(".") > -1 ? "." : ""
  if (splitter) {
    let vals = val.split(splitter)
    valLeftInt = parseInt(vals[0])
    if (vals.length > 1) {
      valRightInt = parseInt(vals[1])
      if (vals[1].length < 2) {
        valRightInt = valRightInt * 10
      }
    }
  } else {
    if (val.length === 3) {
      valLeftInt = parseInt(val.substring(0, 1))
      valRightInt = parseInt(val.substring(1))
    } else if (val.length > 3) {
      valLeftInt = parseInt(val.substring(0, 2))
      valRightInt = parseInt(val.substring(2))
    } else {
      valLeftInt = parseInt(val.substring(0))
    }
  }
  let valid = true
  if (
    isNaN(valLeftInt) ||
    isNaN(valRightInt) ||
    valLeftInt > 23 ||
    valRightInt > 59
  ) {
    valid = false
  }
  if (!valid) {
    ValidationsAdd(field, "SYSValidatorTime", "System")
  } else {
    let valueCorrected = `${valLeftInt
      .toString()
      .padStart(2, "0")}:${valRightInt.toString().padStart(2, "0")}`
    field.value = valueCorrected
    if (asInt) {
      field.convertedValue = parseInt(valueCorrected.replace(":", ""))
    } else {
      field.convertedValue = valueCorrected
    }
  }
}

function toIsoString(date) {
  var tzo = -date.getTimezoneOffset(),
    dif = tzo >= 0 ? "+" : "-",
    pad = function (num) {
      return (num < 10 ? "0" : "") + num
    }

  return (
    date.getFullYear() +
    "-" +
    pad(date.getMonth() + 1) +
    "-" +
    pad(date.getDate()) +
    "T" +
    pad(date.getHours()) +
    ":" +
    pad(date.getMinutes()) +
    ":" +
    pad(date.getSeconds()) +
    dif +
    pad(Math.floor(Math.abs(tzo) / 60)) +
    ":" +
    pad(Math.abs(tzo) % 60)
  )
}

export const _ValidateSchema: SYSAction<ValidateFieldType> = (value) => {
  let field = value.field
  let type = field.type
  // check for size
  if (field.schema.maxLength) {
    if (!field.list) {
      if (type === "text" || type === "int") {
        if (field.value !== null) {
          let val = field.value.toString()
          if (val.length > field.schema.maxLength) {
            let validation: OptionsValidateResultError = {
              translationReps: [field.schema.maxLength],
            }
            ValidationsAdd(field, "SYSValidatorSchemaNrOfChars", "System", {
              validation,
            })
            return
          }
        }
      }
    } else {
      if (type === "text" || type === "int") {
        if (field.convertedValue !== null) {
          let val = field.convertedValue.toString()
          if (val.length > field.schema.maxLength) {
            let validation: OptionsValidateResultError = {
              translationReps: [field.schema.maxLength],
            }
            ValidationsAdd(field, "SYSValidatorSchemaNrOfChars", "System", {
              validation,
            })
            return
          }
        }
      }
    }
  }
}

export const _ValidateList: SYSAction<ValidateFieldType> = (
  value,
  { global }
) => {
  let state = global.getState()
  let actions = global.getActions()
  let field = value.field

  let feature = value.formState.feature
  let list = field.list
  let val = field.convertedValue
  if (
    field.schema.nullValidatorToUse === undefined ||
    !ValidatorCheck(
      field,
      <SYSFormValidationValidators>field.schema.nullValidatorToUse,
      field.schema.nullValidatorOptions?.val
    )
  ) {
    if (val === null || val === "") {
      return
    }
  }
  let functionName = HookFieldGetList.replace(
    "{subFeature}",
    value.formState.subFeature
  ).replace("{fieldKey}", field.fieldKey)
  let fn = actions.app.features[feature]
  let listdata: HookFieldGetList_ReturnType = value.listData
  if (listdata === undefined) {
    if (fn?.hooks?.[functionName]) {
      listdata = _GetFieldList(
        fn.hooks[functionName],
        field.fieldKey,
        value.formState,
        value.formState?.tableDefId,
        undefined,
        true
      )
    } else {
      throw new Error(`Ovl Error: ${functionName} not found!`)
    }
  }

  if (list.acceptOnlyListValues && listdata) {
    let entry = listdata.data[val]
    if (entry === undefined) {
      let validation: ValidateResultError = {}
      if (list.acceptOnlyListValuesValidatorOptions) {
        if (
          list.acceptOnlyListValuesValidatorOptions.displayType !== undefined
        ) {
          validation.displayType =
            list.acceptOnlyListValuesValidatorOptions.displayType
        }
        if (
          list.acceptOnlyListValuesValidatorOptions.summaryDisplayType !==
          undefined
        ) {
          validation.summaryDisplayType =
            list.acceptOnlyListValuesValidatorOptions.summaryDisplayType
        }
        if (
          list.acceptOnlyListValuesValidatorOptions.summaryGroup !== undefined
        ) {
          validation.summaryGroup =
            list.acceptOnlyListValuesValidatorOptions.summaryGroup
        }
        if (
          list.acceptOnlyListValuesValidatorOptions.translationReps !==
          undefined
        ) {
          validation.translationReps =
            list.acceptOnlyListValuesValidatorOptions.translationReps
        }
      }
      // we don't want any other mandatory validation if we have the list validation
      _resetValidationForContext(value.formState, field, "Validate")
      ValidationsAdd(field, "SYSValidatorListValue", "System", { validation })
    }
  }
}

export const _ValidateField = async (
  field: SYSField,
  formState: SYSFormState,
  actions: SYSActions,
  isInnerEvent?: boolean,
  ignoreCustomValidation?: boolean,
  forceValidation?: boolean
) => {
  // console.log(
  //   "Validate Start ",
  //   field.fieldKey,
  //   field.value,
  //   field.convertedValue
  // )
  // create an all fields Validity hash to compare at the end of validate which previousValid and previousConvertedValue should be changed

  // let fieldValidBefore = new Set()
  // Object.keys(formState.fields).forEach((k) => {
  //   if (k === field.fieldKey) {
  //     // don't check itself
  //     return
  //   }
  //   let checkField = formState.fields[k]
  //   if (checkField.inactive && !checkField.inactiveButRelevantForValidation) {
  //   }
  //   if (
  //     checkField.validationResult.System.length +
  //       checkField.validationResult.Validate.length ===
  //     0
  //   ) {
  //     fieldValidBefore.add(checkField.fieldKey)
  //   }
  // })

  _resetValidationForContext(formState, field, "System")
  let feature = formState.feature

  actions.ovl.internal._ValidateDataType({
    field,
    formState,
    isInnerEvent,
  } as ValidateFieldType)

  let fn = actions.app.features[feature]

  if (
    field.validationResult.System.filter((f) => f.resetContext === "System")
      .length === 0
  ) {
    if (!isInnerEvent && field.list) {
      actions.ovl.internal._ValidateList({
        field,
        formState,
        isInnerEvent,
      } as ValidateFieldType)
    }

    if (
      field.validationResult.System.filter((f) => f.resetContext === "System")
        .length === 0
    ) {
      actions.ovl.internal._ValidateSchema({
        field,
        formState,
        isInnerEvent,
      } as ValidateFieldType)

      let changed = true
      if (
        changed &&
        field.validationResult.System.filter((f) => f.resetContext === "System")
          .length === 0
      ) {
        if (field.schema.nullValidatorToUse) {
          _validatorUse(
            field,
            field.schema.nullValidatorToUse,
            "System",
            field.schema.nullValidatorOptions
              ? JSON.parse(JSON.stringify(field.schema.nullValidatorOptions))
              : undefined
          )
        }
      }

      //new 01.11.2025 validator1 also belongs to system group
      if (field.schema.validator1ToUse) {
        _validatorUse(
          field,
          field.schema.validator1ToUse,
          "System",
          field.schema.validator1Options
            ? JSON.parse(JSON.stringify(field.schema.validator1Options))
            : undefined
        )
      }

      // uncommented 31.03.2025 knp
      // because when setCorrectedValue we set this ignoreCustomValidation
      // which should not recursively call itself (thats safe see below if (ignore....))
      // but validation should not be cleared because of that
      // did a research and implementations should be fine

      // if (ignoreCustomValidation || systemValidationCount > 0) {
      //   _resetValidationForContext(formState, field, "Validate")
      // }

      if (
        !SYSConfig.formDefaults.resetValidationContextLogic1CompatibilityMode
      ) {
        // use new style >= 250917
        // introduced because usecase bar:
        // validating config json was not cleared properly
        // or more general: if there were validations set in FormChanged with ResetContext "Validate" and if there was a SystemError after reentering the field then the Validate Context wasn't cleared
        _resetValidationForContext(formState, field, "Validate")
      }

      let systemValidationCount = field.validationResult.System.filter(
        (f) => f.resetContext === "System"
      ).length
      if (
        changed &&
        ignoreCustomValidation !==
          true /* removed because we would also do stuff in the validatehook when a user deletes a value and the field is mandatory&&
        systemValidationCount === 0*/
      ) {
        if (
          SYSConfig.formDefaults.resetValidationContextLogic1CompatibilityMode
        ) {
          // use old style < 250917
          _resetValidationForContext(formState, field, "Validate")
        }

        let hookFormValidate =
          fn?.hooks?.[
            HookFormValidate.replace("{subFeature}", formState.subFeature)
          ]
        if (hookFormValidate) {
          let val: HookFormValidate_Type = {
            field,
            fieldKey: field.fieldKey,
            fields: formState.fields,
            formState,
            validSoFar: systemValidationCount < 1,
            oldValue: field.previousValue,
            newValue: field.value,
            row: GetRowFromFormState(formState),
            setCorrectedValue: async (
              val: any,
              ignoreCustomValidation = true,
              selectValue = false
            ) =>
              await _SetCorrectedValue(
                val,
                field,
                ignoreCustomValidation,
                selectValue
              ),
            tab: field.groups.tab,
            group: field.groups.group,
            validatorUse: (
              validator: SYSFormValidationValidators,
              options?: ValidatorUseOptions
            ): boolean => {
              let resetContext: ValidationResetContext = "Validate"
              if (options?.resetContext) {
                resetContext = options.resetContext
              }
              let fieldToUse = field
              if (options?.field) {
                fieldToUse = options.field
              }
              return _validatorUse(fieldToUse, validator, resetContext, options)
            },
            validatorCheck: (
              validator: SYSFormValidationValidators,
              options?: ValidatorCheckOptions
            ): boolean => {
              let fieldToUse = field
              if (options?.field) {
                fieldToUse = options.field
              }
              return ValidatorCheck(fieldToUse, validator, options?.val)
            },

            validationsAdd: (
              translationKey: string,
              options?: ValidationsAddOptions
            ): void => {
              let fields = options?.fields
              if (fields === undefined) {
                fields = [field]
              }
              let resetContext: ValidationResetContext = "Validate"
              if (options?.resetContext) {
                resetContext = options.resetContext
              }
              _validationsAdd(
                fields,
                translationKey,
                resetContext,
                <ValidatorOptions>options
              )
            },
          }
          await hookFormValidate(val)
        }
      }
    }
    field.previousValue = field.value
  }
}

export const _InitForm: SYSAction<InitFormType, Promise<SYSFormState>> = async (
  value,
  { global }
) => {
  let state = global.getState()
  let actions = global.getActions()
  let instanceId = value.formId
  if (!instanceId) {
    instanceId = value.formType
  }
  try {
    let feature = value.feature
    let subFeature = value.subFeature ? value.subFeature : ""

    let parentField: SYSField
    let parentTableDefId: SYSTableDefIds
    if (value.parentFieldKey) {
      let parentFormState =
        state.ovl.forms[value.parentFormType][value.parentFormId]
      parentField = parentFormState.fields[value.parentFieldKey]
      parentTableDefId = parentFormState.tableDefId
    }
    if (!feature) {
      //    if (actions.app.features && actions.app.features[value.formType]) {
      feature = <SYSFeature>value.formType
      //   }
    }
    let formInstanceList = state.ovl.forms[value.formType]
    if (!formInstanceList) {
      state.ovl.forms[value.formType] = {}
      formInstanceList = state.ovl.forms[value.formType]
    }

    // if there is already a formstate -> do nothing (except its forceOverwrite)
    let formStateCreated = false

    let fn
    fn = actions.app?.features?.[feature]

    let td: _TableGetReturnType
    if (value.tableDefId) {
      td = TableGet(value.tableDefId)
    }
    let schema = undefined
    if (!formInstanceList[instanceId] || value.forceOverwrite === true) {
      value.fields = JSON.parse(JSON.stringify(value.fields))
      formStateCreated = true
      if (td) {
        schema = await TableSchemaGet(td.def.id)
      }

      if (parentField && parentField.editor) {
        let editorInitHookName = `${HookFormEditorInit.replace(
          "{subFeature}",
          subFeature
        )}`
        // we have an editor
        // call the autoInitFields for Editor hook
        let editorInitHook = fn.hooks?.[editorInitHookName]
        if (!editorInitHook) {
          throw Error(
            `Ovl Error: Editor Init Hook ${editorInitHookName} is missing in feature ${feature} hooks`
          )
        }

        let editorInit: HookFormEditorInit_Type = {
          parentField,
          toSet: {
            fieldsToSet: value.fields === undefined ? {} : value.fields,
            groups: [],
          },
          feature,
          formType: value.formType,
          formId: instanceId,
          tableDefId: parentTableDefId,
        }
        await editorInitHook(editorInit)
        if (actionTracking.canceled === true) {
          return
        }
        value.fields = editorInit.toSet.fieldsToSet
        // for editor fields label translation key is given to feature+key
        let prefix = parentField.editor.translationPrefix
        if (!prefix) {
          prefix = ""
        }
        Object.keys(value.fields).forEach((k) => {
          if (!value.fields[k].ui?.noLabel) {
            let transKey = `${feature}${prefix}${subFeature}${parentField.fieldKey}${k}`
            if (!state.ovl.language.translations[transKey]) {
              transKey = `${feature}${prefix}${subFeature}${k}`
              if (!state.ovl.language.translations[transKey]) {
                transKey = `${parentField.editor.id}${prefix}${k}`
                if (!state.ovl.language.translations[transKey]) {
                  transKey = `${parentField.editor.id}${k}`
                }
              }
            }
            if (value.fields[k].ui) {
              value.fields[k].ui.labelTranslationKey = `${transKey}`
            } else {
              value.fields[k].ui = {
                labelTranslationKey: `${transKey}`,
              }
            }
          }
        })

        if (editorInit.toSet.groups && Array.isArray(editorInit.toSet.groups)) {
          parentField._state.groupState = editorInit.toSet.groups
        } else {
          parentField._state.groupState = []
        }
      }

      if (!value._fromAutoInit) {
        // call provide FieldsHook if there
        let autoInitHook = fn?.hooks?.[HookFormAutoInit]

        if (autoInitHook) {
          let autoInit: HookFormAutoInit_Type = {
            formType: value.formType,
            formId: value.formId,
            toSet: {
              fieldsToSet: value.fields === undefined ? {} : value.fields,
              formIdToSet: value.formId,
            },
          }
          await autoInitHook(autoInit)

          value.fields = autoInit.toSet.fieldsToSet
        }
      }
      // initialise schema (get from server) if not already done
      // so fields can be filled with schema default value if necessary

      if (value.tableDataMode === undefined && value.parentFormType) {
        value.tableDataMode =
          state.ovl.forms[value.parentFormType][
            value.parentFormId
          ].tableDataMode
      }
      let fields: FieldValueMap = getFormFields(
        schema,
        value.fields,
        instanceId,
        value.formType,
        feature,
        subFeature,
        value.tableDataMode,
        value.tableDefId,
        value.fillSpaceIfEmptyField
      )

      // default for ValueChanged Hook is per Field (New Style from Sept. 22)
      if (value.doFormChangedForEveryFieldInInit === undefined) {
        value.doFormChangedForEveryFieldInInit = true
      }
      formInstanceList[instanceId] = {
        schema,
        /*dirty: false,*/
        valid: true,
        fields,
        initFields: undefined,
        initialFields: undefined,
        formId: instanceId,
        formType: value.formType,
        feature: feature,
        subFeature: subFeature,
        forceFocusWhenVisibleFieldKey: value.initialFocusFieldKey,
        tableDefId: value.tableDefId,
        tableRowKey: value.tableRowKey,
        tableDataMode: value.tableDataMode,
        row: value.row ? JSON.parse(JSON.stringify(value.row)) : undefined,
        forceKeepFormStateOnCancel: value.forceKeepFormStateOnCancel,
        doFormChangedForEveryFieldInInit:
          value.doFormChangedForEveryFieldInInit,
        isInline: value.isInline,
        locked: false,
        useAlignRight: value.useAlignRight,
        _state: {
          _oldSituation: "",
          _activeEditTab: 0,
          _fieldSetValFromDebouncedInit: undefined,
          _viewHeaderCell: undefined,
          _viewRowCell: undefined,
          _summaryDebouncer: 0,
          _inInitOrChangedCount: 0,
        },
        /* these will be set using defualt validator hook */
        validationDefault: {
          displayType: undefined,
          summaryDisplayType: undefined,
          summaryGroup: undefined,
        },
      }
      if (value.parentFormType) {
        let parent = {
          formType: value.parentFormType,
          formId: value.parentFormId,
          fieldKey: value.parentFieldKey,
        }
        formInstanceList[instanceId].parent = parent
      }

      Object.keys(fields).forEach((f) => {
        // there is no align center for formfields
        // so set those to align left
        if (fields[f].ui.align === "center" || !value.useAlignRight) {
          fields[f].ui.align = "left"
        }

        if (formInstanceList[instanceId].groups === undefined) {
          formInstanceList[instanceId].groups = { edit: {} }
        }

        if (
          formInstanceList[instanceId].groups.edit[fields[f].groups.group] ===
          undefined
        ) {
          formInstanceList[instanceId].groups.edit[fields[f].groups.group] = {
            closing: false,
            inactive: false,
            visible: "true",
          }
        }
      })
      //</defaults>
    }
    let formState = formInstanceList[instanceId]
    formState._state._inInitOrChangedCount++
    formState.initialFields = JSON.parse(JSON.stringify(value.fields))
    let validatorDefaultHook =
      actions.app.global?.hooks?.[HookFormValidatorsDefaults]
    if (validatorDefaultHook) {
      validatorDefaultHook(<HookFormValidatorsDefaults_Type>{
        defaultsToSet: formState.validationDefault,
        formState,
      })
    } else {
      // try on the feature
      let validatorDefaultHook = fn?.hooks?.[HookFormValidatorsDefaults]
      if (validatorDefaultHook) {
        validatorDefaultHook(<HookFormValidatorsDefaults_Type>{
          defaultsToSet: formState.validationDefault,
          formState,
        })
      }
    }

    //delete lookup list cache

    _cachedFieldLists.forEach((_value, key) => {
      if (
        key.startsWith(
          `@@table:${formState.tableDefId ? formState.tableDefId : ""}@@form:${
            formState.formType
          }${formState.formId}`
        )
      ) {
        _cachedFieldLists.set(key, undefined)
      } else if (
        // if its a lookup just used in the table but not in the form we also need to reset it.
        // usecase bar crtificates. enter agropa add a api certificate with numb 676767622 (some inexistent number)
        // the enter bofru and do the same. bofru had just displayed the cardcode in the api cert list because cache was not cleared
        key.startsWith(
          `@@table:${
            formState.tableDefId ? formState.tableDefId : ""
          }@@form:@@fieldkey`
        )
      ) {
        _cachedFieldLists.set(key, undefined)
      }
    })

    // prepare a summary validation error result for each tab
    formState.validationResult = Object.keys(formState.fields).reduce(
      (val, k) => {
        val[formState.fields[k].groups.tab] = {
          summaryErrors: {},
          fieldErrors: [],
          validationKeyErrors: {},
        }
        return val
      },
      {}
    )

    if (formState._state._firstTimeToUpdate === undefined) {
      formState._state._firstTimeToUpdate = formStateCreated
    }
    formState.firstInitOrSituationChanged = formStateCreated
    if (feature) {
      let formInitHook =
        fn?.hooks?.[HookFormInit.replace("{subFeature}", subFeature)]
      if (formInitHook) {
        var initRes: HookFormInit_ReturnType = await formInitHook(<
          HookFormInit_Type
        >{
          formState,
          fields: formState.fields,
        })
        var newSituationObject = { fieldKeys: [], groupKeys: [] }
        if (initRes?.fieldsToRemove?.length > 0) {
          newSituationObject.fieldKeys = initRes.fieldsToRemove
        }
        if (initRes?.groupsToRemove?.length > 0) {
          newSituationObject.groupKeys = initRes.groupsToRemove
        }
        let newSituation = JSON.stringify(newSituationObject)
        if (newSituation !== formState._state._oldSituation) {
          formState.firstInitOrSituationChanged = true
          formState._state._oldSituation = newSituation
        }
        Object.keys(formState.fields)
          .filter(
            (f) =>
              formState.fields[f]._state.removedAndIrrelevantInCurrentSituation
          )
          .forEach((f) => {
            let field = formState.fields[f]
            field._state.removedAndIrrelevantInCurrentSituation = false
            FieldSetState(field, true)
          })

        Object.keys(formState.groups.edit)
          .filter(
            (f) =>
              formState.groups.edit[parseInt(f)]
                .removedAndIrrelevantInCurrentSituation
          )
          .forEach((f) => {
            formState.groups.edit[
              parseInt(f)
            ].removedAndIrrelevantInCurrentSituation = false
          })

        if (initRes) {
          // if there were irrelevants before remove them
          // this enables us to recreate a relevant set everytime we hit forminit
          if (initRes.fieldsToRemove !== undefined) {
            initRes.fieldsToRemove.forEach((fieldKey) => {
              let field = formState.fields[fieldKey]
              //FieldSetState(field, false)
              field.inactive = true
              field._state.visible = false
              field.ui.visible = "false"
              _resetValidationForContext(formState, field, "System")
              _resetValidationForContext(formState, field, "Validate")
              _resetValidationForContext(formState, field, "ValueChanged")
              field._state.removedAndIrrelevantInCurrentSituation = true
              field._state.fieldInitialValidated = true
              //console.log("set irrelevenat", field.fieldKey)
            })
          }

          if (initRes.groupsToRemove !== undefined) {
            initRes.groupsToRemove.forEach((groupKey: string) => {
              formState.groups.edit[
                parseInt(groupKey)
              ].removedAndIrrelevantInCurrentSituation = true
              Object.keys(formState.fields)
                .filter(
                  (f) => formState.fields[f].groups.group === parseInt(groupKey)
                )
                .forEach((f2) => {
                  let field = formState.fields[f2]
                  field.inactive = true
                  field._state.visible = false
                  field.ui.visible = "false"
                  field._state.removedAndIrrelevantInCurrentSituation = true
                })
            })
          }
        }
      }
    }

    //check if there are lists fieldss which have no list match
    // and refresh list automatically
    let fields = formState.fields
    let defColumns = td?.def?.columns
    let endpointListFields = Object.keys(fields).filter((f) => {
      if (
        fields[f].list &&
        fields[f].list.serverEndpoint &&
        fields[f].list.tryGetLookupFromServerInFormInitIfNotFound
      ) {
        let existingList = FieldCurrentList(fields[f])
        let checkValue = fields[f].convertedValue
        if (
          checkValue === null ||
          (fields[f].list
            .tryGetLookupFromServerInFormInitIfNotFoundIgnoreValues !==
            "None" &&
            fields[
              f
            ].list.tryGetLookupFromServerInFormInitIfNotFoundIgnoreValues.indexOf(
              checkValue
            ) > -1)
        ) {
          return false
        }
        if (existingList.data[checkValue] !== undefined) {
          // in addition if one of the lookup column is undefined it needs to be completed as well using this method
          return Object.keys(existingList.lookupDef).some(
            (s) => existingList.data[checkValue][s] === undefined
          )
        } else {
          return true
        }
      } else {
        return false
      }
    })
    await Promise.all(
      endpointListFields.map((m) =>
        FieldList(fields[m], {
          forceNoSnack: true,
          notFullList: true,
        })
      )
    )
    _RenderTracking.pauseTracking = true
    if (formState.firstInitOrSituationChanged) {
      let fieldKeys = Object.keys(formState.fields)
        .filter((f) => !formState.fields[f]._state.fieldInitialValidated)
        .sort((a, b) => SortGet(defColumns?.[a]?.order, defColumns?.[b]?.order))

      for (let i = 0; i < fieldKeys.length; i++) {
        let field = formState.fields[fieldKeys[i]]
        // if (field.fieldKey === "Presort") {
        //   debugger
        // }
        if (!field.inactive || field.inactiveButRelevantForValidation) {
          if (field.list) {
            field.value = field.convertedValue
          }
          // // 10.04.25 knp, BAR activities. Duplicate an activity which has not default contact set was setting always default contact
          // // turned out validValueChanged was wrong in that case
          if (
            formState.row &&
            formState.row[field.fieldKey] === field.convertedValue
          ) {
            field.previousConvertedValue = field.convertedValue
          }
          // make sure we have a ui.labelCaption
          await GetFieldLabel(field, formState.feature, field.ui.labelCaption)
          field._state.fieldInitialValidated = true
          await _ValidateField(field, formState, actions, false, false, true)

          let isValid = FieldIsValidSelective(field)
          if (isValid) {
            field.previousValid = true
            field.previousValidConvertedValue = field.convertedValue
          }
        }
      }
      //setFormDirty(formState)
    }
    // // if the field in the underlying data is different than the one on the form its considered dirty
    if (formStateCreated && !formState.initFields) {
      formState.initFields = JSON.parse(
        JSON.stringify(formState.fields),
        stringifyReplacer
      )
    }
    if (formState.doFormChangedForEveryFieldInInit) {
      let fieldKeys = Object.keys(formState.fields).sort((a, b) =>
        SortGet(defColumns?.[a]?.order, defColumns?.[b]?.order)
      )
      for (let i = 0; i < fieldKeys.length; i++) {
        let field = formState.fields[fieldKeys[i]]
        if (!field._state.fieldInitialChanged) {
          let isValid = FieldIsValidSelective(field)

          let validValueChanged =
            field.previousConvertedValue !== field.convertedValue && isValid
              ? true
              : false
          let changedParams: FieldValueChanged = {
            field,
            isValid,
            fieldKey: field.fieldKey,
            oldValue: field.previousConvertedValue,
            newValue: field.convertedValue,
            oldValidValue: field.previousValidConvertedValue,
            validValueChanged,
            fromInit: true,
            group: field.groups.group,
            tab: field.groups.tab,
          }
          let hookFormChanged =
            fn?.hooks?.[
              HookFormChanged.replace("{subFeature}", formState.subFeature)
            ]

          if (hookFormChanged) {
            await hookFormChanged(<HookFormChanged_Type>{
              formState,
              fields: formState.fields,
              row: GetRowFromFormState(formState),
              validatorUse: (
                validator: SYSFormValidationValidators,
                options?: ValidatorUseOptions
              ): boolean => {
                let resetContext: ValidationResetContext = "Validate"
                if (options?.resetContext) {
                  resetContext = options.resetContext
                }
                let fieldToUse = field
                if (options?.field) {
                  fieldToUse = options.field
                }
                return _validatorUse(
                  fieldToUse,
                  validator,
                  resetContext,
                  options
                )
              },
              validatorCheck: (
                validator: SYSFormValidationValidators,
                options?: ValidatorCheckOptions
              ): boolean => {
                let fieldToUse = field
                if (options?.field) {
                  fieldToUse = options.field
                }
                return ValidatorCheck(fieldToUse, validator, options?.val)
              },
              validationsAdd: (
                translationKey: string,
                options?: ValidationsAddOptions
              ): void => {
                let fields = options?.fields
                if (fields === undefined) {
                  fields = [field]
                }
                let resetContext: ValidationResetContext = "Validate"
                if (options?.resetContext) {
                  resetContext = options.resetContext
                }
                _validationsAdd(
                  fields,
                  translationKey,
                  resetContext,
                  <ValidatorOptions>options
                )
              },
              validationsRemove: (
                fields: SYSField | SYSField[],
                validationKey: string
              ) => {
                if (fields === undefined) {
                  fields = [field]
                }
                ValidationsRemove(fields, validationKey)
              },
              setCorrectedValue: async (
                val: any,
                ignoreCustomValidation = false,
                selectValue = false
              ) =>
                await _SetCorrectedValue(
                  val,
                  field,
                  ignoreCustomValidation,
                  selectValue
                ),

              changedField: changedParams,
            })
          }
          field._state.fieldInitialChanged = true
        }
      }
    }

    // support for old style form init handling
    // is disabled by default...
    if (!formState.doFormChangedForEveryFieldInInit) {
      let hookFormChanged =
        fn?.hooks?.[
          HookFormChanged.replace("{subFeature}", formState.subFeature)
        ]

      if (hookFormChanged) {
        await hookFormChanged({
          formState,
          fields: formState.fields,
          row: GetRowFromFormState(formState),
          initiatedByFormInit: true,
        })
      }
    }

    formState.firstInitOrSituationChanged = false
    Object.keys(formState.fields).forEach((k) => {
      _setFieldValid(formState.fields[k])
    })
    _setFormValid(formState)
    setVisibleValidation(formState, 0)
    setRowCellInformation(formState, actions, state)
    if (
      td?.def.options.edit.noCancelOnEditFormIfFormStateWasInvalidWhenOpened &&
      formState.tableDataMode === "Update" &&
      !FormIsValid(formState)
    ) {
      formState._state._noCancelOnEditFormIfFormStateWasInvalidWhenOpened = true
    }
    formState._state._inInitOrChangedCount--

    return formState
  } catch (e) {
    // we need to remove any invalid formstate already created
    delete ovl.getState().ovl.forms[value.formType][instanceId]
    throw e
  }
}
export type ValidateFieldType = {
  value: any
  field: SYSField
  correctedValueToSet: any
  formState: SYSFormState
  isInnerEvent: boolean
  listData?: ListDefinition
}

export type ChangeFieldType = {
  formState: SYSFormState
  fieldKey: string
  value: any
  // if initilised touched and dirty should not be set
  watched?: boolean
  // if innerEvent then just shortcircuit some validation and just do value update (textbox char entry -> every key gets recorded -> keeps state in value but not convertedValue)
  // this also means if innerevent is false then for sure the editing is done and lost focus
  isInnerEvent?: boolean

  // make oboslete? see FieldSetVal API.
  //  the submitted value is already the finished converted and user checked value
  // so no validation will happen and eventually the form valid will be set to true
  ignoreCustomValidation?: boolean
  // make oboslete? see FieldSetVal API.
  // we don't want the value changed to be fired
  ignoreValueChanged?: boolean

  // triggered will not be overwritten by subsequent FieldSetVal in the same process if set to true (prevent endless loops)
  noRetrigger?: boolean
}

export type SetField = {
  formState: SYSFormState
  fieldKey: string
  convertedValue: any
  setToWatched?: boolean
}

export type FieldChanged = {
  formState: SYSFormState
  field: SYSField
  convertedValue: any
  oldConvertedValue: any
  value: any
  oldValue: any
  row: any
}

export type _FocusField = {
  field: SYSField
  hasFocus: boolean
}

export const _FocusField: SYSAction<_FocusField> = (
  { field, hasFocus },
  { global }
) => {
  let formState = ovl.getState().ovl.forms[field.formType][field.formId]
  if (!formState) {
    return
  }
  field.hasFocus = hasFocus
  if (hasFocus) {
    _setFieldWatched(field, true)
    formState.fieldToFocus = field.fieldKey
  } else {
    _setFieldTouched(field, false)
  }
}

export const _WatchField: SYSAction<{ field: SYSField; watched: boolean }> = (
  { field, watched },
  { global }
) => {
  let state = global.getState()
  field.watched = watched
  // if (!ValidationContext.mode) {
  //   setVisibleValidation(ovl.getState().ovl.forms[field.formType][field.formId])
  // }
}

// this method is just used to get the tooltips and custom class names when in form-context
export const setRowCellInformation = (
  formState: SYSFormState,
  actions: SYSActions,
  state: SYSState
) => {
  let columns: { [key: string]: ColumnDisplayDef } =
    getColumnDefsFromFormState(formState)
  let displayMode: EditDisplayMode = formState.isInline ? "EditInline" : "Edit"
  let resolveFn = actions.app.features[formState.feature]
  let def
  if (formState.tableDefId) {
    def = TableGet(formState.tableDefId).def
  }

  if (resolveFn) {
    let hookViewRowCellClass =
      resolveFn.hooks?.[
        HookViewRowCellClass.replace("{subFeature}", formState.subFeature)
      ]
    if (hookViewRowCellClass) {
      let viewRowCellParams: HookViewRowCellClass_Type = {
        columns,
        displayMode,
        rowKey: formState.tableRowKey,
        row: GetRowFromFormState(formState),
        feature: formState.feature,
        tableDefId: formState.tableDefId,
        def,
      }
      let res = hookViewRowCellClass(viewRowCellParams)
      // only set it if different
      // elsewise a lot of rerendering could happen
      if (
        JSON.stringify(res) !== JSON.stringify(formState._state._viewRowCell)
      ) {
        formState._state._viewRowCell = res
      }
    }
  }
  if (resolveFn) {
    let hookViewHeaderCellClass =
      resolveFn.hooks?.[
        HookViewHeaderCellClass.replace("{subFeature}", formState.subFeature)
      ]

    if (hookViewHeaderCellClass) {
      let viewHeaderCellParams: HookViewHeaderCellClass_Type = {
        columns,
        displayMode,

        feature: formState.feature,
        tableDefId: formState.tableDefId,
      }

      let res = hookViewHeaderCellClass(viewHeaderCellParams)
      if (
        JSON.stringify(res) !== JSON.stringify(formState._state._viewHeaderCell)
      ) {
        formState._state._viewHeaderCell = res
      }
    }
  }
}

let changedParams: FieldValueChanged
// we need that barrier to check in FieldIsValid or FormIsValid to call the full shabang
/* changefield itself should not be async cuz it happens a lot */
/* if you need to do async validation use the changed hooks */
export const _ChangeField: SYSAction<ChangeFieldType> = async (
  value,
  { global }
) => {
  value.formState._state._inInitOrChangedCount++
  let state = global.getState()
  let actions = global.getActions()
  if (!value.formState.validationResult) {
    throw Error(
      "SetField/ChangeField can only be used on an finished, initialised form"
    )
  }
  let field: SYSField = value.formState.fields[value.fieldKey]

  field._state.valueChangedHandled

  //field._state.valueCorrected = false

  if (field._state.noRetrigger) {
    // if we are in the middle of a user triggered change and the field get changed again in between
    // we don't allow that
    return
  }
  try {
    field._state.noRetrigger = value.noRetrigger
    value.isInnerEvent = value.isInnerEvent === true

    // important for our Validators to know
    if (value.watched !== undefined) {
      let _watched = value.watched === true
      _setFieldWatched(field, _watched)
    }
    // => we want userinputs to be reflected immeditaely in the ui (imagine a fast typer)
    StartScreenUpdate(0)

    field.value = value.value
    // if (field.fieldKey === "TOTPDigit1") {
    //   debugger
    // }
    if (field.list && !value.isInnerEvent) {
      // this is currently the way to bust the list cache...
      _GetFieldList(
        undefined,
        field.fieldKey,
        value.formState,
        value.formState?.tableDefId,
        true,
        true
      )
    }

    //if (value.isCorrectedValue) {
    //   // if its a corrected value just do the DataType Check (Which also handles converted Value)
    //   // and also make sure all recorded errors on thies field are resetted
    //   _resetValidationForContext(value.formState, field, "System")
    //   _resetValidationForContext(value.formState, field, "Validate")
    //   actions.ovl.internal._ValidateDataType({
    //     field,
    //     formState: value.formState,
    //     isInnerEvent: value.isInnerEvent,
    //   } as ValidateFieldType)
    // } else {
    await _ValidateField(
      field,
      value.formState,
      actions,
      value.isInnerEvent,
      value.ignoreCustomValidation
    )
    //}

    field._state.fieldInitialValidated = true

    if (value.isInnerEvent) {
      _setFieldTouched(field, true)
    } else {
      _setFieldTouched(field, false)
    }
    let fromInit =
      value.formState._state._fieldSetValFromDebouncedInit ||
      value.formState.firstInitOrSituationChanged

    if (!value.isInnerEvent || field._state.forceChange) {
      let feature = value.formState.feature
      let fn = actions.app.features[feature]

      let newValForChanged
      let oldValForChanged
      let changed = false
      let isValid = FieldIsValidSelective(field)
      let validValueChanged = !!field._state.forceChange

      if (
        field.convertedValue !== field.previousConvertedValue ||
        field.previousValid !== isValid ||
        (isValid && field.previousValidConvertedValue !== field.convertedValue)
      ) {
        field._state.forceChange = false
        newValForChanged = field.convertedValue
        oldValForChanged = field.previousConvertedValue
        changed = true
      }

      if (field._state.forceChange) {
        field._state.forceChange = false
        newValForChanged = field.convertedValue
        oldValForChanged = field.previousConvertedValue
        validValueChanged = true
        changed = true
      }

      if (changed /*&& !value.ignoreValueChanged*/) {
        let oldValidValue = field.previousValidConvertedValue
        //@ts-ignore

        if (
          isValid &&
          field.previousValidConvertedValue !== field.convertedValue
        ) {
          validValueChanged = true
        }

        _resetValidationForContext(value.formState, field, "ValueChanged")
        isValid = FieldIsValidSelective(field)
        if (value.ignoreValueChanged !== true) {
          changedParams = {
            field,
            fieldKey: field.fieldKey,
            isValid,
            oldValue: oldValForChanged,
            newValue: newValForChanged,
            oldValidValue,
            validValueChanged,
            fromInit,
            group: field.groups.group,
            tab: field.groups.tab,
          }

          // console.log(
          //   `value: ${field.value}, ${field.previousValue}, converted: ${field.convertedValue}, ${field.previousConvertedValue}, previousConvertedValid: ${field.previousValidConvertedValue}`
          // )
          // console.log(changedParams)

          let hookFormChanged =
            fn?.hooks?.[
              HookFormChanged.replace(
                "{subFeature}",
                value.formState.subFeature
              )
            ]
          if (hookFormChanged) {
            await hookFormChanged(<HookFormChanged_Type>{
              formState: value.formState,
              fields: value.formState.fields,
              row: GetRowFromFormState(value.formState),
              validatorUse: (
                validator: SYSFormValidationValidators,
                options?: ValidatorUseOptions
              ): boolean => {
                let resetContext: ValidationResetContext = "ValueChanged"
                if (options?.resetContext) {
                  resetContext = options.resetContext
                }
                let fieldToUse = field
                if (options?.field) {
                  fieldToUse = options.field
                }

                return _validatorUse(
                  fieldToUse,
                  validator,
                  resetContext,
                  options
                )
              },
              validatorCheck: (
                validator: SYSFormValidationValidators,
                options?: ValidatorCheckOptions
              ): boolean => {
                let fieldToUse = field
                if (options?.field) {
                  fieldToUse = options.field
                }
                return ValidatorCheck(fieldToUse, validator, options?.val)
              },

              validationsAdd: (
                translationKey: string,
                options?: ValidationsAddOptions
              ): void => {
                let fields = options?.fields
                if (fields === undefined) {
                  fields = [field]
                }
                let resetContext: ValidationResetContext = "ValueChanged"
                if (options?.resetContext) {
                  resetContext = options.resetContext
                }
                _validationsAdd(
                  fields,
                  translationKey,
                  resetContext,
                  <ValidatorOptions>options
                )
              },
              validationsRemove: (
                fields: SYSField | SYSField[],
                validationKey: string
              ) => {
                ValidationsRemove(fields, validationKey)
              },
              setCorrectedValue: async (
                val: any,
                ignoreCustomValidation = false,
                selectValue = false
              ) =>
                await _SetCorrectedValue(
                  val,
                  field,
                  ignoreCustomValidation,
                  selectValue
                ),

              changedField: changedParams,
              initiatedByFormInit: false,
            })
          }

          // if (field._state.valueCorrected) {
          //   return
          // }

          field._state.fieldInitialChanged = true
        }

        field.previousConvertedValue = field.convertedValue
        let newValid = FieldIsValidSelective(field)
        if (newValid) {
          field.previousValidConvertedValue = field.convertedValue
        }
        // this was field.previousValid = true and inside the if above
        // if changing test usecase kaltAG import zuschnittliste with errors that got added in ValueChanged but have resetcontext Validate
        field.previousValid = newValid
      }
    }
    // if (!field._state.removedAndIrrelevantInCurrentSituation) {
    //   //let oldDirty = field.dirty

    //   let convertedValue = field.convertedValue
    //   let originalValue = field.originalValue

    //   if (field.type === "object") {
    //     // we need a sortedreplacer because elsewise we cannot compare because the order of props is different
    //     convertedValue = JSON.stringify(convertedValue, sortedReplacer)
    //     originalValue = JSON.stringify(originalValue, sortedReplacer)
    //   }

    //   //   field.dirty =
    //   //     /* new 18-04.2024 if tableDataMode = Add don't set dirty on init
    //   //    this enables canceling the form without prompting if the user really didn't change anything
    //   //    usecase: casic "Neuer Auftrag" -> cancel -> there should be no prompt even the docdate was initialised to todays date
    //   // */
    //   //     (value.formState.tableDataMode !== "Add" || !fromInit) &&
    //   //     !field.schema?.isCalculatedColumn &&
    //   //     convertedValue !== originalValue

    //   //   // if (oldDirty !== field.dirty) {
    //   //   //   setFormDirty(value.formState)
    //   //   // }
    // }
  } catch (err) {
    if (err.type !== "OvlServerOffline") {
      SnackAdd(err.message, "Error")
    }
  } finally {
    field._state.noRetrigger = false
    setVisibleValidation(value.formState)
    _setFieldValid(field)
    _setFormValid(value.formState)
    setRowCellInformation(value.formState, actions, state)
    value.formState._state._inInitOrChangedCount--
  }
}

// unclear filter and usage is low in projects (TimePortal)
// better access field validation directly

// export const GetFormValidationErrors = (formState: SYSFormState): string[] => {
//   let res: string[] = []
//   Object.keys(formState.fields).map((k) => {
//     let field = formState.fields[k]
//     res = res.concat(
//       field.validationResult.System.concat(
//         field.validationResult.Validate.concat(
//           field.validationResult.ValueChanged
//         )
//       )
//         .filter((f) => f.displayType !== "OnlyOutline")
//         .map((m) => T(m.translationKey, m.translationReps))
//     )
//   })
//   res = res.concat(
//     formState.validationResult.errors.map((m) =>
//       T(m.translationKey, m.translationReps)
//     )
//   )
//   return res
// }
